import { ChangeDetectionStrategy, Component, inject, Injector } from '@angular/core'

import { combineLatest, map, startWith, takeUntil } from 'rxjs'

import { CountryCodes, LanguageCode, TspView } from '@ti/contracts'
import { TspProfile } from '@ti/core/auth/services'
import { Memoize } from '@ti/core/common/decorators'
import { injectDestroy$, toSignalState, wrap$ } from '@ti/core/common/utils'
import { CONFIG } from '@ti/core/contracts/config'
import { PROFILE } from '@ti/core/contracts/profile'

@Component({ template: '', changeDetection: ChangeDetectionStrategy.OnPush })
export abstract class BaseLangSwitchDialog {
  protected readonly config = inject(CONFIG)
  protected readonly injector = inject(Injector)
  protected readonly profile = inject(PROFILE)
  protected readonly tspProfile = inject(TspProfile, { optional: true })
  protected readonly destroy$ = injectDestroy$()

  protected readonly LABELS = {
    [LanguageCode.English]: 'English',
    [LanguageCode.Spanish]: 'Español',
    [LanguageCode.Portuguese]: 'Português',
    [LanguageCode.Nederlands]: 'Dutch',
  } as Record<LanguageCode, string>

  protected readonly NL_COUNTRIES = [CountryCodes.Netherlands, CountryCodes.Belgium]

  @Memoize()
  public get state() {
    return toSignalState(this.state$, this.injector)
  }

  @Memoize()
  public get state$() {
    return {
      options: this.createLanguageOptionsStream(),
    }
  }

  protected createLanguageOptionsStream() {
    return this.createAllowedLanguagesStream().pipe(
      map((languageCodes) => {
        return languageCodes.map((languageCode) => {
          return {
            label: this.LABELS[languageCode],
            value: languageCode,
          }
        })
      }),
      takeUntil(this.destroy$),
    )
  }

  protected createAllowedLanguagesStream() {
    const profile$ = this.profile.state.pipe(startWith(undefined))
    const tspProfile$ = this.tspProfile
      ? this.tspProfile.tsp$.pipe(startWith(undefined))
      : wrap$<TspView | undefined>(undefined)

    return combineLatest([profile$, tspProfile$]).pipe(
      map(([profile, tspProfile]) => {
        const options = [LanguageCode.English, LanguageCode.Spanish]
        const isProduction = this.config.envType === 'production'

        const country = (profile?.fleet?.country || tspProfile?.country) as CountryCodes | undefined
        if (!isProduction || !country || (country && this.NL_COUNTRIES.includes(country))) {
          options.push(LanguageCode.Nederlands)
        }

        if (!isProduction) {
          options.push(LanguageCode.Portuguese)
        }
        return options
      }),
      takeUntil(this.destroy$),
    )
  }
}
